import { HTTP_STATUS } from '../domain/domainCodes.js'
/**
 * The DataTables contract, both directions — the single most-copied piece of wiring in
 * this migration (W1).
 *
 * Every list endpoint in the product answers the DataTables 1.13 server-side shape:
 *
 *   { Draw, RecordsTotal, RecordsFiltered, Data }        // and `Error` on failure
 *
 * The REQUEST side is not uniform, and reading the endpoint before writing the service
 * is the only way to know which of the two it wants (roadmap §5, step 1):
 *
 *   BODY  `Users/Filter` binds `[FromBody] { DataParams: {...}, Type, GroupId, ... }`
 *         — a nested JSON object (`Features/Admin/UsersManagement/Endpoint.cs:113`).
 *   FORM  `/api/admin/invitations/filter` calls `ReadFormAsync()` and reads the flat
 *         jQuery field names through `DataTableMapper`
 *         (`Features/Admin/Invitations/Endpoint.cs:36`).
 *
 * Both are built from ONE state object, so a screen never knows which style its
 * endpoint uses — only its service module does.
 */

import { ApiError } from './api'
import { toFormData } from './form'

/** `DataTableMapper` clamps `length` to 0..100, so asking for more silently gets 100. */
export const MAX_PAGE_SIZE = 100

/**
 * THE RESPONSE COMES BACK IN TWO CASINGS, and both have to be read.
 *
 * `PropertyNamingPolicy` is null, so a property is serialised exactly as it is named in C#.
 * Endpoints that return the typed `DataTablesResponse<T>` therefore answer PascalCase
 * (`Data`, `RecordsFiltered`) — `/Users/Filter` and most others. But seven slices hand-build
 * the envelope as an anonymous object with lowercase names:
 *
 *   Results.Json(new { draw = …, recordsTotal = …, recordsFiltered = …, data = … })
 *
 * — `GroupsManagement`, `UsersGroups`, `CategoriesManagement`, `LearningProgramsReports`,
 * `SystemState`, `InAppNotifications` and `Leaderboard`. Reading only one casing means
 * every list in those slices comes back silently EMPTY: no error, no rows, just a table
 * that says there is nothing there. `readDataTable` accepts either (R2 — the UI adapts).
 */

/**
 * @typedef {object} ListState
 * @property {number} [page]      1-based, the way the pager counts
 * @property {number} [pageSize]
 * @property {string} [search]
 * @property {{ key: string, dir: 'asc'|'desc' }} [sort]
 * @property {string[]} [columns]  column keys IN ORDER. The wire carries the sort as an
 *                                 INDEX into this list, and the server reads the NAME at
 *                                 that index (`columns[i][data]`) — so `sort.key` must be
 *                                 one of these to mean anything, and the names must be the
 *                                 ones the handler's sort map declares
 * @property {number} [draw]       echo counter; only jQuery ever cared, kept for parity
 */

/** The neutral middle form. Both builders below start here. */
function normalise({ page = 1, pageSize = 10, search = '', sort, columns = [], draw = 1 } = {}) {
  const length = Math.min(Math.max(pageSize, 1), MAX_PAGE_SIZE)
  const sortIndex = sort ? columns.indexOf(sort.key) : -1

  return {
    draw,
    start: (Math.max(page, 1) - 1) * length,
    length,
    search: search ?? '',
    columns,
    // An unknown sort key would otherwise order by column 0 without saying so.
    order: sortIndex >= 0 ? { column: sortIndex, dir: sort.dir === 'desc' ? 'desc' : 'asc' } : null,
  }
}

/**
 * The nested `DataParams` object, for endpoints binding `[FromBody]`.
 * PascalCase, because that is what the server's `DataTablesRequest` deserialises.
 */
export function dataParams(state) {
  const { draw, start, length, search, columns, order } = normalise(state)

  return {
    Draw: draw,
    Start: start,
    Length: length,
    Search: { Value: search, Regex: false },
    Order: order ? [{ Column: order.column, Dir: order.dir }] : [],
    Columns: columns.map((key) => ({
      Data: key,
      Name: key,
      Searchable: true,
      Orderable: true,
      Search: { Value: '', Regex: false },
    })),
  }
}

/**
 * The flat jQuery field names, for endpoints calling `ReadFormAsync()`.
 *
 * The bracket syntax is not decorative: `DataTableMapper` looks up
 * `columns[0][data]` and `search[value]` literally, and stops walking columns the
 * moment `columns[i][data]` is missing.
 *
 * @param {ListState} state
 * @param {object} [extra]  the slice's own filters, appended alongside
 */
export function dataParamsForm(state, extra = {}) {
  const { draw, start, length, search, columns, order } = normalise(state)
  const form = new FormData()

  form.append('draw', String(draw))
  form.append('start', String(start))
  form.append('length', String(length))
  form.append('search[value]', search)
  form.append('search[regex]', 'false')

  columns.forEach((key, index) => {
    form.append(`columns[${index}][data]`, key)
    form.append(`columns[${index}][name]`, key)
    form.append(`columns[${index}][searchable]`, 'true')
    form.append(`columns[${index}][orderable]`, 'true')
    form.append(`columns[${index}][search][value]`, '')
    form.append(`columns[${index}][search][regex]`, 'false')
  })

  if (order) {
    form.append('order[0][column]', String(order.column))
    form.append('order[0][dir]', order.dir)
  }

  return toFormData(extra, form)
}

/**
 * The same flat field names as `dataParamsForm`, as a URL-encoded body — for the many
 * small lists that already post `URLSearchParams` and whose handlers read the form either
 * way. `extra` is the slice's own filters, appended alongside; empty values are left out.
 *
 * @param {ListState} state
 * @param {Record<string, string|number|null|undefined>} [extra]
 */
export function dataParamsQuery(state, extra = {}) {
  const { draw, start, length, search, columns, order } = normalise(state)
  const params = new URLSearchParams({
    draw: String(draw),
    start: String(start),
    length: String(length),
    'search[value]': search,
    'search[regex]': 'false',
  })

  columns.forEach((key, index) => {
    params.set(`columns[${index}][data]`, key)
    params.set(`columns[${index}][name]`, key)
    params.set(`columns[${index}][searchable]`, 'true')
    params.set(`columns[${index}][orderable]`, 'true')
  })

  if (order) {
    params.set('order[0][column]', String(order.column))
    params.set('order[0][dir]', order.dir)
  }

  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }

  return params
}

/**
 * The response, in the shape the shared `DataTable` already consumes.
 *
 * `total` is `RecordsFiltered` — the count the pager must divide by. `RecordsTotal` is
 * kept separately because a few screens show "12 of 340" and would otherwise have to
 * re-read the raw payload.
 */
export function readDataTable(response) {
  /*
   * `Error` IS THE FAILURE, and it arrives with HTTP 200.
   *
   * Every DataTables handler in this product catches its own exceptions and answers
   * `{ Draw, RecordsTotal: 0, RecordsFiltered: 0, Data: [], Error: "<ex.Message>" }`. The
   * status code never reports it. Reading `Data` and ignoring `Error` therefore turned a
   * database fault into an empty list, and the screen told the operator "no records found"
   * — the opposite of the truth, on every server-paged list in the product.
   *
   * Thrown as an `ApiError` so it lands in the same catch as every other failure and the
   * screen renders its error state rather than its empty one.
   */
  const failure = response?.Error ?? response?.error
  if (failure) {
    throw new ApiError(String(failure), { status: HTTP_STATUS.OK, code: 'RESULT', data: response })
  }

  const rows = response?.Data ?? response?.data ?? []
  return {
    rows,
    total: response?.RecordsFiltered ?? response?.recordsFiltered ?? rows.length,
    totalUnfiltered: response?.RecordsTotal ?? response?.recordsTotal ?? 0,
    draw: response?.Draw ?? response?.draw ?? 0,
  }
}
